import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Banner } from "../../../_components/Contacts/ContactShared";
import FinanceDocList from "../../../_components/Finance/FinanceDocList";
import {
    getPurchaseInvoices,
    updatePurchaseInvoice,
} from "../../../_services/purchase.service";
import {
    getPaymentVouchers,
    updatePaymentVoucher,
    getCreditNotes,
    updateCreditNote,
    getDebitNotes,
    updateDebitNote,
    getPurchaseCreditNotes,
    updatePurchaseCreditNote,
    getPurchaseDebitNotes,
    updatePurchaseDebitNote,
} from "../../../_services/finance.service";
import {
    FiClock,
    FiUser,
    FiDollarSign,
    FiHome,
    FiCheckCircle,
    FiXCircle,
    FiFileText
} from "react-icons/fi";
import { T, fmtMYR } from "../../../_components/Sales/SalesShared";
import { DocStatusBadge as FinanceStatusBadge } from "../../../_components/Finance/FinanceShared";
import { isAdmin, isAccountant } from "../../../utils/roleConstants";
import config from "../../../../config";

const APPROVAL_STATUSES = ["All", "Pending for Approval", "Approved", "Rejected"];

const FINANCE_SEARCH_OPTIONS = [
    { key: "all", label: "All" },
    { key: "id", label: "Doc No." },
    { key: "date", label: "Date" },
    { key: "supplier", label: "Company Name" },
    { key: "contactPerson", label: "Contact Person" }
];

export default function FinanceApprovals() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [refreshKey, setRefreshKey] = useState(0);
    const [banner, setBanner] = useState({ type: "", msg: "" });
    const [approvalRouting, setApprovalRouting] = useState([]);

    const user = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem("user") || "{}");
        } catch (e) {
            return {};
        }
    }, []);

    const showBanner = (type, msg) => {
        setBanner({ type, msg });
        setTimeout(() => setBanner({ type: "", msg: "" }), 4000);
    };

    useEffect(() => {
        if (!searchParams.get("status")) {
            setSearchParams(
                {
                    status: "Pending for Approval",
                    page: "1"
                },
                { replace: true }
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch Finance approval routing
    useEffect(() => {
        if (user && user.accountId) {
            fetch(`${config.apiUrl}/getAccountingApprovalRouting?account_id=${user.accountId}`)
                .then((res) => {
                    if (!res.ok) throw new Error("Failed to fetch routing");
                    return res.json();
                })
                .then((data) => {
                    setApprovalRouting(Array.isArray(data) ? data : []);
                })
                .catch((err) => {
                    console.error("Error fetching finance approval routing:", err);
                    setApprovalRouting([]);
                });
        }
    }, [user]);

    const getFinanceApprovals = useCallback(
        async (qp) => {
            const p = { ...qp, page: 1, pageSize: 1500 };

            const [piRes, pvRes, pcnRes, pdnRes, cnRes, dnRes] = await Promise.all([
                getPurchaseInvoices(p).catch(() => ({ data: [] })),
                getPaymentVouchers(p).catch(() => ({ data: [] })),
                getPurchaseCreditNotes(p).catch(() => ({ data: [] })),
                getPurchaseDebitNotes(p).catch(() => ({ data: [] })),
                getCreditNotes(p).catch(() => ({ data: [] })),
                getDebitNotes(p).catch(() => ({ data: [] })),
            ]);

            let combined = [
                ...(piRes.data || []).map((x) => ({
                    ...x,
                    _docType: x.piType === "Direct" ? "Direct Purchase Invoice" : "Indirect Purchase Invoice",
                    _basePath: x.piType === "Direct" ? "/finance/direct-purchase-invoices" : "/finance/indirect-purchase-invoices",
                    _typeKey: "purchase_invoice",
                })),
                ...(pvRes.data || []).map((x) => ({
                    ...x,
                    _docType: "Payment Voucher",
                    _basePath: "/finance/payment-vouchers",
                    _typeKey: "payment_voucher",
                })),
                ...(pcnRes.data || []).map((x) => ({
                    ...x,
                    _docType: "Purchase Credit Note",
                    _basePath: "/finance/purchase-credit-notes",
                    _typeKey: "purchase_credit_note",
                })),
                ...(pdnRes.data || []).map((x) => ({
                    ...x,
                    _docType: "Purchase Debit Note",
                    _basePath: "/finance/purchase-debit-notes",
                    _typeKey: "purchase_debit_note",
                })),
                ...(cnRes.data || []).map((x) => ({
                    ...x,
                    _docType: "Sales Credit Note",
                    _basePath: "/finance/credit-notes",
                    _typeKey: "sales_credit_note",
                })),
                ...(dnRes.data || []).map((x) => ({
                    ...x,
                    _docType: "Sales Debit Note",
                    _basePath: "/finance/debit-notes",
                    _typeKey: "sales_debit_note",
                })),
            ].filter((x) => {
                if (!qp.status || qp.status === "All") return true;
                return x.status === qp.status;
            });

            if (qp.search) {
                const lower = qp.search.toLowerCase();
                combined = combined.filter((x) => {
                    if (qp.searchType === "all") {
                        return Object.values(x).some((v) => String(v).toLowerCase().includes(lower));
                    }
                    return String(x[qp.searchType] || "").toLowerCase().includes(lower);
                });
            }

            if (qp.sortBy) {
                combined.sort((a, b) => {
                    let va = a[qp.sortBy] || "";
                    let vb = b[qp.sortBy] || "";
                    if (va < vb) return qp.sortDir === "asc" ? -1 : 1;
                    if (va > vb) return qp.sortDir === "asc" ? 1 : -1;
                    return 0;
                });
            }

            const total = combined.length;
            const start = (qp.page - 1) * qp.pageSize;
            const paginated = combined.slice(start, start + qp.pageSize);

            return {
                data: paginated,
                pagination: { total, page: qp.page, pageSize: qp.pageSize }
            };
        },
        [refreshKey]
    );

    const handleApproveRejectFinance = useCallback(
        async (item, action, onClose, reason = null) => {
            try {
                const newStatus = action === "reject" ? "Rejected" : "Approved";

                if (item._typeKey === "purchase_invoice") {
                    await updatePurchaseInvoice(item.id, { status: newStatus, rejectionReason: reason });
                } else if (item._typeKey === "payment_voucher") {
                    await updatePaymentVoucher(item.id, { status: newStatus, rejectionReason: reason });
                } else if (item._typeKey === "purchase_credit_note") {
                    await updatePurchaseCreditNote(item.id, { status: newStatus, rejectionReason: reason });
                } else if (item._typeKey === "purchase_debit_note") {
                    await updatePurchaseDebitNote(item.id, { status: newStatus, rejectionReason: reason });
                } else if (item._typeKey === "sales_credit_note") {
                    await updateCreditNote(item.id, { status: newStatus, rejectionReason: reason });
                } else if (item._typeKey === "sales_debit_note") {
                    await updateDebitNote(item.id, { status: newStatus, rejectionReason: reason });
                }

                onClose();
                setRefreshKey((k) => k + 1);
                window.dispatchEvent(new CustomEvent("refreshPendingApprovalCounts"));
                showBanner(
                    "success",
                    `Document ${item.id} ${action === "reject" ? "rejected" : "approved"} successfully.`
                );
            } catch (err) {
                console.error(err);
                showBanner("danger", `Failed to process document ${item.id}.`);
            }
        },
        []
    );

    const isDesignatedApprover = useMemo(() => {
        return approvalRouting.some(
            (route) => route.approverId && parseInt(route.approverId) === parseInt(user.id)
        );
    }, [approvalRouting, user]);

    const isAuthorized = useMemo(() => {
        return (isAdmin(user) || isAccountant(user)) && isDesignatedApprover;
    }, [user, isDesignatedApprover]);

    const renderFinancePreviewActions = useCallback(
        (item, onClose, onView) => {
            return (
                <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%" }}>
                    {item.status === "Pending for Approval" && isAuthorized && (
                        <div style={{ display: "flex", gap: 10, width: "100%" }}>
                            <button
                                onClick={() => handleApproveRejectFinance(item, "approve", onClose)}
                                style={{
                                    flex: 1,
                                    padding: "9px 12px",
                                    background: T.success,
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 8,
                                    fontWeight: 600,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 6,
                                    cursor: "pointer"
                                }}
                            >
                                <FiCheckCircle size={14} /> Approve
                            </button>
                            <button
                                onClick={() => {
                                    const reason = window.prompt("Please provide a reason for rejection:");
                                    if (reason === null) return;
                                    if (!reason.trim()) {
                                        alert("Rejection reason is required.");
                                        return;
                                    }
                                    handleApproveRejectFinance(item, "reject", onClose, reason);
                                }}
                                style={{
                                    flex: 1,
                                    padding: "9px 12px",
                                    background: "#fff",
                                    color: T.danger,
                                    border: `1.5px solid ${T.danger}`,
                                    borderRadius: 8,
                                    fontWeight: 600,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 6,
                                    cursor: "pointer"
                                }}
                            >
                                <FiXCircle size={14} /> Reject
                            </button>
                        </div>
                    )}

                    <button
                        className="cl-preview-btn-primary"
                        onClick={() => onView(`${item._basePath}/${item.id}?fromApprovals=true`)}
                        style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                            padding: "12px 16px",
                            fontSize: 14
                        }}
                    >
                        <FiFileText size={16} /> View Document Details
                    </button>
                </div>
            );
        },
        [handleApproveRejectFinance, isAuthorized]
    );

    const financeColumns = useMemo(
        () => [
            {
                label: "Doc. No.",
                key: "id",
                width: 110,
                render: (v) => (
                    <span style={{ color: T.textLight, fontWeight: 700, fontSize: 13 }}>{v}</span>
                )
            },
            {
                label: "Type",
                key: "_docType",
                width: 160,
                render: (v) => (
                    <span style={{ fontWeight: 600, color: T.accent, fontSize: 13 }}>{v}</span>
                )
            },
            {
                label: "Date",
                key: "date",
                width: 110,
                render: (v) => <span style={{ color: T.textMuted, fontSize: 13 }}>{v}</span>
            },
            {
                label: "Company Name",
                key: "supplier",
                width: 200,
                render: (v) => (
                    <span
                        style={{
                            fontWeight: 600,
                            display: "inline-block",
                            maxWidth: 190,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                        }}
                    >
                        {v}
                    </span>
                )
            },
            {
                label: "Amount",
                key: "grandTotal",
                render: (v, row) => (
                    <span style={{ fontWeight: 700, color: T.textPrimary }}>
                        {fmtMYR(v ?? row.payAmount)}
                    </span>
                )
            },
            { label: "Status", key: "status", render: (v) => <FinanceStatusBadge value={v} /> }
        ],
        []
    );

    const financePreviewFields = useMemo(
        () => [
            { icon: FiFileText, label: "Document Type", key: "_docType" },
            { icon: FiClock, label: "Date", key: "date" },
            { icon: FiHome, label: "Company Name", key: "supplier" },
            { icon: FiUser, label: "Contact Person", key: "contactPerson" },
            { icon: FiDollarSign, label: "Amount", key: "grandTotal", render: (v, row) => fmtMYR(v ?? row.payAmount) }
        ],
        []
    );

    return (
        <div style={{ padding: "0 20px" }}>
            <Banner type={banner.type} msg={banner.msg} onClose={() => setBanner({ type: "", msg: "" })} />

            <div style={{ marginBottom: 20 }}>
                <h4 style={{ fontWeight: 700, color: T.textPrimary, margin: 0, fontSize: 24 }}>
                    Finance & Accounting Approvals
                </h4>
                <p style={{ fontSize: 14, color: T.textMuted, margin: "4px 0 0" }}>
                    Review and process finance documents: Purchase Invoices, Payment Vouchers, Debit & Credit Notes.
                </p>
            </div>

            <FinanceDocList
                docType="Finance Document"
                basePath="/accounting"
                fetchList={getFinanceApprovals}
                statuses={APPROVAL_STATUSES}
                customColumns={financeColumns}
                renderPreviewActions={renderFinancePreviewActions}
                hideCreate={true}
                hideExport={true}
                searchOptions={FINANCE_SEARCH_OPTIONS}
                previewFields={financePreviewFields}
            />
        </div>
    );
}